import { Link } from 'react-router-dom';
import { MapPin, ArrowRight } from 'lucide-react';
import { slugify } from '@/lib/seo';

const REGION_BLURB = {
  Maharashtra: 'Jowar and bajra bhakri, besan-based pithla and zunka, sprouted usals and goda-masala rice dishes.',
  Goa: 'Coconut and kokum-led coastal cooking — fish curry rice, xacuti, cafreal, balchao and pao breakfasts.',
  Kerala: 'Rice and coconut breakfasts such as appam, puttu and idiyappam, plus coconut-milk stews and fish curries.',
};

/**
 * Crawlable regional cuisine index — real recipe links grouped by region.
 * Gives the home page genuine internal linking into every regional recipe page.
 */
export const CuisineLinks = ({ recipes = [] }) => {
  const byRegion = recipes.reduce((acc, r) => {
    if (!r.region) return acc;
    (acc[r.region] = acc[r.region] || []).push(r);
    return acc;
  }, {});
  const regions = Object.keys(byRegion).sort();
  if (regions.length === 0) return null;

  return (
    <section id="cuisines" className="py-20 px-6" style={{ background: '#FDFBF7' }}>
      <div className="max-w-6xl mx-auto">
        <div className="max-w-2xl mb-12">
          <p className="text-xs uppercase tracking-widest text-[#F97316] font-['Outfit'] font-semibold mb-3">
            Regional cuisines
          </p>
          <h2 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#1C1917] tracking-tight mb-4">
            Browse Indian cooking by region
          </h2>
          <p className="text-base text-[#1C1917]/60 font-['Outfit'] leading-relaxed">
            Every regional dish on Masala Spin has its own recipe page with ingredients, method,
            approximate time and a calorie estimate. Start with a region and work through it.
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-3">
          {regions.map((region) => {
            const list = byRegion[region];
            return (
              <div
                key={region}
                data-testid={`cuisine-block-${region.toLowerCase()}`}
                className="p-6 rounded-3xl bg-white border border-[#1C1917]/5"
              >
                <h3 className="font-['Playfair_Display'] text-2xl font-bold text-[#1C1917] mb-2 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#F97316]" aria-hidden="true" />
                  {region}
                </h3>
                <p className="text-sm text-[#1C1917]/60 font-['Outfit'] leading-relaxed mb-4">
                  {REGION_BLURB[region] || `${list.length} recipes from ${region}.`}
                </p>
                <ul className="space-y-1.5 font-['Outfit'] text-sm">
                  {list.slice(0, 8).map((r) => (
                    <li key={r.id || r.name}>
                      <Link
                        to={`/recipe/${slugify(r.name)}`}
                        data-testid={`cuisine-link-${slugify(r.name)}`}
                        className="text-[#1C1917]/75 hover:text-[#F97316] transition-colors inline-flex items-baseline gap-1.5"
                      >
                        {r.name}
                        <span className="text-[11px] text-[#1C1917]/40">{r.prep_time}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-xs text-[#1C1917]/45 font-['Outfit']">
                  {list.length} {region} recipes in total
                </p>
              </div>
            );
          })}
        </div>

        <div className="mt-10 font-['Outfit'] text-sm">
          <Link
            to="/blog"
            data-testid="cuisines-to-blog"
            className="inline-flex items-center gap-1.5 text-[#F97316] font-semibold hover:gap-2.5 transition-all"
          >
            Read our regional cooking guides <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
};
